import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, Plus, Pencil, Trash2, ChevronDown,
  HelpCircle, Info, BookOpen, AlertTriangle,
  MessageCircleQuestion, Code2, GitBranch,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { THEME, pageCategoryStyle } from "@/config/theme";
import { CV } from "@/config/canvasSizing";
import {
  listNodePages, createNodePage, deleteNodePage, buildPageTree, dropPageFromTree,
  type NodePage, type NodePageWithChildren, type PageCategory,
} from "@/services/nodePageService";
import { useNodePageStore, type PanelTreatment } from "@/store/useNodePageStore";

// ── Treatment presentation (per design mock) ────────────────────────────────────
// The outer wrapper positions/animates the panel over the canvas; pointerEvents
// stays off the wrapper so the canvas around the card is still interactive.
function wrapperStyle(t: PanelTreatment, entered: boolean): React.CSSProperties {
  const base: React.CSSProperties = { position: "absolute", zIndex: 35, display: "flex", pointerEvents: "none" };
  if (t === "panel") {
    return {
      ...base, top: CV.pagesPanel.panel.top, right: 0, bottom: 0, width: CV.pagesPanel.panel.width,
      transform: entered ? "translateX(0)" : "translateX(104%)",
      opacity: entered ? 1 : 0,
      transition: "transform .6s cubic-bezier(.5,.1,.15,1), opacity .4s",
    };
  }
  if (t === "glass") {
    return {
      ...base, top: "50%", right: CV.pagesPanel.glass.right, width: CV.pagesPanel.glass.width, maxHeight: CV.pagesPanel.glass.maxHeight,
      transform: `translateY(-50%) scale(${entered ? 1 : 0.94})`,
      opacity: entered ? 1 : 0,
      transition: "opacity .45s ease, transform .55s cubic-bezier(.4,1.4,.5,1)",
    };
  }
  // drawer (default) — bottom-centre sheet
  return {
    ...base, left: "50%", bottom: CV.pagesPanel.drawer.bottom, width: CV.pagesPanel.drawer.width, height: CV.pagesPanel.drawer.height,
    transform: `translateX(-50%) translateY(${entered ? "0" : "26px"})`,
    opacity: entered ? 1 : 0,
    transition: "opacity .45s ease, transform .55s cubic-bezier(.4,1.2,.5,1)",
  };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES: { value: PageCategory; label: string; icon: LucideIcon }[] = [
  { value: "question",  label: "Question",  icon: HelpCircle },
  { value: "info",      label: "Info",      icon: Info },
  { value: "deep-dive", label: "Deep Dive", icon: BookOpen },
  { value: "critical",  label: "Critical",  icon: AlertTriangle },
];

function categoryEntry(cat: PageCategory) {
  return CATEGORIES.find((c) => c.value === cat) ?? CATEGORIES[1];
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NodePageDrawerProps {
  nodeId: string;
  nodeLabel: string;
  initialPageId?: string | null;
  onClose: () => void;
}

// ── Content preview ─────────────────────────────────────────────────────────────
// The page body is stored as tiptap HTML. The codebase never injects raw HTML
// (no dangerouslySetInnerHTML anywhere — a deliberate XSS-safe posture), so the
// read card shows a plain-text preview: DOMParser never executes scripts and we
// only read textContent. Block tags become newlines so paragraphs/lists don't run
// together.
function htmlToText(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.body.querySelectorAll("p, div, li, br, h1, h2, h3, blockquote, pre").forEach((el) => {
    el.after(doc.createTextNode("\n"));
  });
  return (doc.body.textContent ?? "").replace(/\n{2,}/g, "\n").trim();
}

// ── Section label (mono, uppercase, per-section accent hue) ─────────────────────

function SectionLabel({ icon: Icon, color, children }: { icon?: LucideIcon; color: string; children: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-1.5 text-[10px] font-bold mb-1.5 ${THEME.pageCard.label} ${color}`}>
      {Icon && <Icon size={12} />}
      {children}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function NodePageDrawer({ nodeId, nodeLabel, initialPageId, onClose }: NodePageDrawerProps) {
  const { getToken } = useAuth();
  const storeSetPages = useNodePageStore((s) => s.setPages);
  const upsertPage    = useNodePageStore((s) => s.upsertPage);
  const removePage    = useNodePageStore((s) => s.removePage);
  const openFocusMode = useNodePageStore((s) => s.openFocusMode);
  const treatment     = useNodePageStore((s) => s.treatment);
  const focusMode     = useNodePageStore((s) => s.focusMode);

  const cachedPages = useNodePageStore((s) => s.pagesByNode[nodeId]);

  const [tree, setTree] = useState<NodePageWithChildren[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(!cachedPages);
  const [deleteTarget, setDeleteTarget] = useState<NodePage | null>(null);
  // Flip on after mount so the wrapper animates in (slide/scale/fade per treatment).
  const [entered, setEntered] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Fetch pages from API and update both local tree and store.
  const fetchPages = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return listNodePages(getToken, nodeId)
      .then((loaded) => {
        setTree(buildPageTree(loaded));
        storeSetPages(nodeId, loaded);
      })
      .catch(console.error)
      .finally(() => { if (!silent) setLoading(false); });
  }, [nodeId, getToken, storeSetPages]);

  // On open: use prefetched data instantly, then background-refresh from API.
  useEffect(() => {
    if (cachedPages) {
      setTree(buildPageTree(cachedPages));
      setLoading(false);
      fetchPages(true);
    } else {
      fetchPages();
    }
    if (initialPageId) setExpanded(new Set([initialPageId]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // When the focus editor closes (open→closed), silently re-read pages so
  // freshly authored summary/code/asks appear without reopening the panel.
  const focusWasOpen = useRef(false);
  useEffect(() => {
    const isOpen = focusMode !== null;
    if (focusWasOpen.current && !isOpen) fetchPages(true);
    focusWasOpen.current = isOpen;
  }, [focusMode, fetchPages]);

  // Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Smooth scroll the toggled card into view after the expand animation starts
    requestAnimationFrame(() => {
      const card = scrollContainerRef.current?.querySelector(`[data-page-id="${id}"]`) as HTMLElement | null;
      if (card?.scrollIntoView) {
        card.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
  }, []);

  // Editing happens in the full-screen focus editor (mock's separate modal).
  const editPage = useCallback((p: NodePage, mode: "view" | "edit") => {
    openFocusMode({
      pageId: p.id,
      nodeId,
      nodeLabel,
      initialTitle: p.title,
      initialCategory: p.category,
      initialContent: p.content,
      initialMode: mode,
    });
  }, [openFocusMode, nodeId, nodeLabel]);

  const handleAddPage = useCallback(async () => {
    try {
      const created = await createNodePage(getToken, nodeId, "New page", "info", "");
      setTree((prev) => [...prev, { ...created, children: [] }]);
      // Pre-expand it so the authored fields are visible the moment the editor closes.
      setExpanded((prev) => new Set(prev).add(created.id));
      upsertPage(nodeId, created);
      editPage(created, "edit");
    } catch (err) {
      console.error(err);
    }
  }, [getToken, nodeId, upsertPage, editPage]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    try {
      await deleteNodePage(getToken, id);
      removePage(nodeId, id);
      // Drop the page whether it's a top-level card or a nested sub-subject.
      setTree((prev) => dropPageFromTree(prev, id));
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
    }
  }, [deleteTarget, getToken, nodeId, removePage]);

  const topCount = tree.length;

  const isPanel = treatment === "panel";
  return (
    <div style={wrapperStyle(treatment, entered)} data-testid="node-page-panel-wrap" data-treatment={treatment}>
    <div
      className={`relative w-full flex flex-col overflow-y-auto rounded-[20px] ${
        treatment === "glass" ? THEME.pageCard.shellGlass : THEME.pageCard.shell
      } ${isPanel ? "rounded-tr-none rounded-br-none rounded-bl-none" : ""}`}
      style={{ maxHeight: "100%", pointerEvents: "auto" }}
      data-testid="node-page-drawer"
    >
      {/* Header — icon-wrap + mono kicker + node label + subtitle + actions */}
      <div className={`flex items-start gap-3 px-4 py-3.5 ${THEME.panel.borderBottom} shrink-0`}>
        <div className="shrink-0 w-9 h-9 flex items-center justify-center rounded-[11px] bg-indigo-500/15">
          <BookOpen size={18} className="text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`${THEME.pageCard.drawerKicker} truncate`}>
            Node · what you learned
          </p>
          <p className={`${THEME.pageCard.drawerTitle} truncate mt-0.5`}>{nodeLabel}</p>
          <p className="text-[11.5px] text-indigo-400 font-medium mt-0.5">
            {topCount} {topCount === 1 ? "subject" : "subjects"} discussed in this session
          </p>
        </div>
        <button
          data-testid="add-page-btn"
          onClick={handleAddPage}
          className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-500/35 bg-indigo-500/[0.14] text-indigo-300 text-[11.5px] font-semibold hover:bg-indigo-500/25 transition-colors"
        >
          <Plus size={14} />
          New page
        </button>
        <button
          onClick={onClose}
          data-testid="drawer-close"
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-white/10 text-canvas-muted hover:text-canvas-text hover:bg-white/5 transition-colors"
        >
          <X size={15} />
        </button>
      </div>

      {/* Body — scrollable column of expandable page cards */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2.5 bg-[#111218]/95 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-white/20 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10">
        {loading && <p className="text-[11px] text-canvas-muted px-1 py-2">Loading…</p>}
        {!loading && tree.length === 0 && (
          <p className="text-[13px] font-light text-canvas-muted px-1 py-2">No pages yet — add one to capture what you learned.</p>
        )}
        {!loading && tree.map((p) => (
          <PageCard
            key={p.id}
            page={p}
            open={expanded.has(p.id)}
            onToggle={() => toggle(p.id)}
            onEdit={() => editPage(p, "edit")}
            onDelete={() => setDeleteTarget(p)}
          />
        ))}
      </div>

      {/* Delete confirmation overlay */}
      {deleteTarget && (
        <div className="absolute inset-0 bg-canvas-node/90 flex items-center justify-center z-50 p-6">
          <div className="bg-canvas-node border border-canvas-node-border rounded-xl shadow-xl p-5 w-full max-w-xs">
            <p className="text-sm font-semibold text-canvas-text mb-1">Delete page?</p>
            <p className="text-xs text-canvas-muted mb-4">
              &ldquo;{deleteTarget.title || "Untitled"}&rdquo; will be permanently deleted.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                data-testid="confirm-delete-btn"
                className="flex-1 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-1.5 rounded-lg border border-canvas-node-border text-xs text-canvas-text hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

// ── Expandable page card ────────────────────────────────────────────────────────

function PageCard({
  page, open, onToggle, onEdit, onDelete,
}: {
  page: NodePageWithChildren;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const pageId = page.id;
  const cat = categoryEntry(page.category);
  const CatIcon = cat.icon;
  const catStyle = pageCategoryStyle(page.category);
  const contentText = htmlToText(page.content);
  const asks = page.asks ?? [];
  const children = page.children ?? [];
  const oneLine = page.summary || asks[0] || contentText || "";
  const hasDetails =
    Boolean(contentText) || Boolean(page.summary) || asks.length > 0 ||
    Boolean(page.code) || children.length > 0;

  return (
    <div
      data-page-id={pageId}
      className={`rounded-xl ${THEME.pageCard.surface}`}
      style={{
        display: "grid",
        gridTemplateRows: `auto ${open ? "1fr" : "0fr"}`,
        transition: "grid-template-rows 550ms cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      {/* Collapsed row (always visible, row 1) */}
      <div
        className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer ${THEME.pageCard.surfaceHover} transition-colors`}
        onClick={onToggle}
      >
        <span className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg ${catStyle.tint}`}>
          <CatIcon size={16} className={catStyle.color} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-semibold text-canvas-text truncate">{page.title || "Untitled"}</span>
            <span className={`shrink-0 text-[9.5px] font-mono font-semibold uppercase tracking-[0.1em] px-2 py-0.5 rounded ${catStyle.pill}`}>
              {cat.label}
            </span>
          </div>
          {oneLine && (
            <p className={`text-[12px] text-canvas-muted truncate mt-0.5 transition-opacity duration-200 ${open ? "opacity-0 h-0 overflow-hidden" : "opacity-100"}`}>
              {oneLine}
            </p>
          )}
        </div>
        <button
          data-testid={`edit-page-${page.id}`}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-canvas-muted hover:text-canvas-text hover:bg-white/5 transition-colors"
          title="Edit page"
        >
          <Pencil size={14} />
        </button>
        <button
          data-testid={`delete-page-${page.id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          title="Delete page"
        >
          <Trash2 size={14} />
        </button>
        <ChevronDown
          size={16}
          className={`shrink-0 text-canvas-muted transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </div>

      {/* Expanded body (row 2 — animates 0fr → 1fr) */}
      <div
        className={`overflow-hidden min-h-0 ${
          open ? "opacity-100" : "opacity-0"
        } transition-opacity duration-400 ease-[cubic-bezier(0.4,0,0.2,1)]`}
      >
        <div className="px-3.5 pb-3.5 pt-0.5 pl-12 flex flex-col gap-3">
          {!hasDetails && (
            <p className="text-[12.5px] text-canvas-muted italic">
              No details captured yet — click the pencil to add a summary, snippet or questions.
            </p>
          )}

          {contentText && (
            <div>
              <SectionLabel color="text-indigo-500">Your notes</SectionLabel>
              <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap ${THEME.pageCard.summaryText} select-text`}>
                {contentText}
              </p>
            </div>
          )}
          {!contentText && page.summary && (
            <div>
              <SectionLabel color="text-indigo-500">AI summary</SectionLabel>
              <p className={`text-[13.5px] leading-relaxed ${THEME.pageCard.summaryText} select-text`}>{page.summary}</p>
            </div>
          )}

          {page.summary && (
            <div>
              <SectionLabel color={THEME.pageCard.summary}>Summary</SectionLabel>
              <p className={`text-[13.5px] leading-relaxed ${THEME.pageCard.summaryText} select-text`}>{page.summary}</p>
            </div>
          )}

          {asks.length > 0 && (
            <div>
              <SectionLabel icon={MessageCircleQuestion} color="text-violet-400">You asked</SectionLabel>
              <div className="flex flex-col gap-1.5">
                {asks.map((q, i) => (
                  <div key={i} className="text-[13px] leading-snug px-2.5 py-1.5 rounded-r-md text-[#d4d4d8] bg-violet-400/[0.08] border-l-2 border-violet-400/50 select-text">
                    {q}
                  </div>
                ))}
              </div>
            </div>
          )}

          {page.code && (
            <div>
              <SectionLabel icon={Code2} color="text-zinc-500">Snippet</SectionLabel>
              <pre className={`m-0 text-[12.5px] leading-relaxed font-mono rounded-lg px-3 py-2.5 overflow-x-auto whitespace-pre ${THEME.pageCard.snippetBlock} select-text`}>
                {page.code}
              </pre>
            </div>
          )}

          {children.length > 0 && (
            <div>
              <SectionLabel icon={GitBranch} color="text-cyan-400">
                Sub-subjects · {children.length}
              </SectionLabel>
              <div className={`flex flex-col gap-2.5 pl-3.5 border-l-[1.5px] ${THEME.pageCard.childRail} select-text`}>
                {children.map((c) => <SubSubject key={c.id} page={c} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Nested sub-subject (read-only) ──────────────────────────────────────────────

function SubSubject({ page }: { page: NodePage }) {
  const cat = categoryEntry(page.category);
  const CatIcon = cat.icon;
  const catStyle = pageCategoryStyle(page.category);
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md ${catStyle.tint}`}>
          <CatIcon size={12} className={catStyle.color} />
        </span>
        <span className="text-[13.5px] font-semibold text-canvas-text truncate">{page.title || "Untitled"}</span>
        <span className={`shrink-0 text-[9px] font-mono font-semibold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded ${catStyle.pill}`}>
          {cat.label}
        </span>
      </div>
      {page.summary && <p className="text-[12.5px] leading-snug text-canvas-muted pl-8 mt-0.5 select-text">{page.summary}</p>}
      {page.code && (
        <code className={`inline-block mt-1.5 ml-8 text-[11.5px] font-mono rounded-md px-2 py-1 ${THEME.pageCard.snippetBlock} select-text`}>
          {page.code}
        </code>
      )}
    </div>
  );
}
